// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

// Models
import { SiteID } from '../../sites/models/site'
import { EmployeeID } from '../../employees/models/employee'
import { EmployeeShiftOffer, ShiftOfferStatus } from '../models/employeeShiftOffer'
import { Pagination } from '../../common/models/pagination'

// Services
import { add, EmployeeShiftOfferSearchForm, fetch, fetchByEmployee, remove, update } from '../employeeShiftOfferService'
import { ShiftID } from '../../shifts/models/shift'
import { employeeKeys } from '../../employees/composables/useEmployeeQuery'
import { Area } from '../../areas/models/area'
import { DateTime } from 'luxon'
import { useEventStore } from '../../common/eventStore'
import { shiftKeys } from '../../shifts/composables/useShift'
import { Skill } from '../../skills/models/skill'

interface EmployeeShiftOfferFilters {
    dateFrom?: MaybeRefOrGetter<DateTime>
    dateTo?: MaybeRefOrGetter<DateTime>

    status?: MaybeRefOrGetter<string>

    area?: MaybeRefOrGetter<Area | null>
    skill?: MaybeRefOrGetter<Skill | null>
    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const employeeShiftOfferKeys = {
    all: ['employee-shift-offers'] as const,
    lists: () => [...employeeShiftOfferKeys.all, 'list'] as const,
    list: (filters: EmployeeShiftOfferFilters) => [...employeeShiftOfferKeys.lists(), { area: filters.area, skill: filters.skill, perPage: filters.perPage, page: filters.page }] as const,
    listByEmployee: (employeeId: MaybeRefOrGetter<EmployeeID>, filters: EmployeeShiftOfferFilters) => [...employeeShiftOfferKeys.lists(), employeeId, { dateFrom: filters.dateFrom, dateTo: filters.dateTo, area: filters.area, skill: filters.skill, perPage: filters.perPage, page: filters.page }] as const,
}

export function useEmployeeShiftOfferQuery(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>, filters: EmployeeShiftOfferFilters = {}) {
    return useQuery<{ offers: EmployeeShiftOffer[], pagination: Pagination }, Error>({
        queryKey: employeeShiftOfferKeys.list(filters),
        queryFn: async () => {
            const form: EmployeeShiftOfferSearchForm = {
                perPage: filters.perPage ? toValue(filters.perPage) : 15,
                page: filters.page ? toValue(filters.page) : 1,
            }

            const area = toValue(filters.area)
            if (area) {
                form.areaId = area.id
            }

            const skill = toValue(filters.skill)
            if (skill) {
                form.skillId = skill.id
            }

            return fetch(toValue(siteId), toValue(shiftId), form)
        },
        placeholderData: keepPreviousData,
    })
}

export function useEmployeeShiftOfferAddMutation(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, { form: { employeeId: EmployeeID } }>({
        mutationFn: async (data) => {
            return add(toValue(siteId), toValue(shiftId), data.form.employeeId)
        },
        onSettled: () => {
            // TODO: Fix invalidate only for the shift we're modifying
            queryClient.invalidateQueries({
                queryKey: employeeShiftOfferKeys.lists(),
            })
            queryClient.invalidateQueries({
                queryKey: employeeKeys.lists(),
            })
        }
    })
}

export function useEmployeeShiftOfferRemoveMutation(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, { form: { employeeId: EmployeeID } }>({
        mutationFn: async (data) => {
            return remove(toValue(siteId), toValue(shiftId), data.form.employeeId)
        },
        onSettled: () => {
            // TODO: Fix invalidate only for the shift we're modifying
            queryClient.invalidateQueries({
                queryKey: employeeShiftOfferKeys.lists(),
            })
            queryClient.invalidateQueries({
                queryKey: employeeKeys.lists(),
            })
        }
    })
}

export function useEmployeeShiftOfferUpdateMutation(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<unknown, Error, { form: { employeeId: EmployeeID, status: ShiftOfferStatus } }>({
        mutationFn: async (data) => {
            return update(toValue(siteId), toValue(shiftId), data.form.employeeId, { status: data.form.status })
        },
        onSettled: () => {
            // TODO: Fix invalidate only for the shift we're modifying
            queryClient.invalidateQueries({
                queryKey: employeeShiftOfferKeys.lists(),
            })
            queryClient.invalidateQueries({
                queryKey: employeeKeys.lists(),
            })
            queryClient.invalidateQueries({
                queryKey: shiftKeys.detail(shiftId),
            })
        },
        onSuccess: () => {
            // TODO: We don't have access to the employee name or shift information here
            // TODO: We could change this style depending on the action we take?
            eventStore.addToast({ severity: 'info', summary: 'Updated' })
        },
        onError: (error) => {
            eventStore.addErrorToast({ detail: error.message })
        }
    })
}

export function useEmployeeShiftOfferByEmployeeQuery(siteId: MaybeRefOrGetter<SiteID>, employeeId: MaybeRefOrGetter<EmployeeID>, filters: EmployeeShiftOfferFilters = {}, enabled: MaybeRefOrGetter<boolean> = true) {
    return useQuery<{ offers: EmployeeShiftOffer[], pagination: Pagination }, Error>({
        queryKey: employeeShiftOfferKeys.listByEmployee(employeeId, filters),
        queryFn: async () => {
            const form: EmployeeShiftOfferSearchForm = {
                perPage: filters.perPage ? toValue(filters.perPage) : 15,
                page: filters.page ? toValue(filters.page) : 1,
            }

            const dateFrom = toValue(filters.dateFrom)
            if (dateFrom) {
                form.dateFrom = dateFrom
            }

            const dateTo = toValue(filters.dateTo)
            if (dateTo) {
                form.dateTo = dateTo
            }

            const status = toValue(filters.status)
            if (status) {
                form.status = status
            }

            const area = toValue(filters.area)
            if (area) {
                form.areaId = area.id
            }

            const skill = toValue(filters.skill)
            if (skill) {
                form.skillId = skill.id
            }

            return fetchByEmployee(toValue(siteId), toValue(employeeId), form)
        },
        placeholderData: keepPreviousData,
        enabled: toValue(enabled),
    })
}